import {
  parseQuestionResolution,
  splitTimelineStages,
  timelineStageAnswerText
} from 'src/components/chat'
import type {
  ApprovalResolution,
  QuestionResolution,
  TimelineItem
} from 'src/components/chat'

import type { SquadStreamEvent } from './squadAgent'

export {
  splitTimeline,
  splitTimelineStages,
  timelineStageAnswerText
} from 'src/components/chat'
export type { TimelineStage } from 'src/components/chat'

const toDetail = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }

  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export const parseJsonObject = (
  raw: unknown
): Record<string, unknown> | undefined => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  if (typeof raw !== 'string') return undefined
  try {
    const parsed = JSON.parse(raw)

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined
  } catch {
    return undefined
  }
}

type PersistedQuestion = {
  approvalId: string
  question: string
  options: string[]
  allowMultiple: boolean
}

const parsePersistedQuestion = (
  approvalId: string,
  rawArgs: unknown
): PersistedQuestion => {
  const args = parseJsonObject(rawArgs)

  return {
    approvalId,
    question: String(args?.question ?? ''),
    options: Array.isArray(args?.options) ? args.options.map(String) : [],
    allowMultiple: Boolean(args?.allow_multiple)
  }
}

export const appendStreamEvent = (
  timeline: TimelineItem[],
  event: SquadStreamEvent
): TimelineItem[] => {
  const last = timeline[timeline.length - 1]

  switch (event.type) {
    case 'token':
      if (last?.kind === 'text') {
        return [
          ...timeline.slice(0, -1),
          { kind: 'text', text: last.text + event.text }
        ]
      }

      return [...timeline, { kind: 'text', text: event.text }]
    case 'reasoning':
      if (last?.kind === 'reasoning') {
        return [
          ...timeline.slice(0, -1),
          { kind: 'reasoning', text: last.text + event.text }
        ]
      }

      return [...timeline, { kind: 'reasoning', text: event.text }]
    case 'tool_call': {
      const payload = event.payload as
        | { args?: unknown; id?: unknown }
        | undefined
      const id = payload?.id ? String(payload.id) : undefined

      const alreadyStreamed =
        id &&
        timeline.some((item) => item.kind === 'tool_call' && item.id === id)
      if (alreadyStreamed) return timeline

      return [
        ...timeline,
        {
          kind: 'tool_call',
          name: event.name || undefined,
          args: toDetail(payload?.args),
          id
        }
      ]
    }
    case 'tool_result': {
      const payload = event.payload as
        | { output?: unknown; tool_call_id?: unknown }
        | undefined
      const id = payload?.tool_call_id
        ? String(payload.tool_call_id)
        : undefined

      const alreadyStreamed =
        id &&
        timeline.some((item) => item.kind === 'tool_result' && item.id === id)
      if (alreadyStreamed) return timeline

      return [
        ...timeline,
        {
          kind: 'tool_result',
          name: event.name || undefined,
          output: toDetail(payload?.output),
          id
        }
      ]
    }
    case 'approval_required': {
      const payload = event.payload as
        | { approval_id?: unknown; tool?: unknown; value?: unknown }
        | undefined
      const approvalId = event.approvalId ?? String(payload?.approval_id ?? '')
      const value = payload?.value as
        | {
            type?: unknown
            question?: unknown
            options?: unknown
            allow_multiple?: unknown
          }
        | undefined

      if (value?.type === 'ask_user_question') {
        return [
          ...timeline,
          {
            kind: 'question',
            approvalId,
            question: String(value.question ?? ''),
            options: Array.isArray(value.options)
              ? value.options.map(String)
              : [],
            allowMultiple: Boolean(value.allow_multiple),
            status: 'pending'
          }
        ]
      }

      return [
        ...timeline,
        {
          kind: 'approval',
          approvalId,
          tool: payload?.tool ? String(payload.tool) : 'tool',
          value: payload?.value as Record<string, unknown> | undefined,
          status: 'pending'
        }
      ]
    }
    default:
      return timeline
  }
}

export const markApprovalResolved = (
  timeline: TimelineItem[],
  approvalId: string,
  resolution: ApprovalResolution
): TimelineItem[] =>
  timeline.map((item) =>
    item.kind === 'approval' && item.approvalId === approvalId
      ? { ...item, status: 'resolved', resolution }
      : item
  )

export const enrichApprovalValue = (
  timeline: TimelineItem[],
  approvalId: string,
  patch: Record<string, unknown>
): TimelineItem[] =>
  timeline.map((item) =>
    item.kind === 'approval' && item.approvalId === approvalId
      ? { ...item, value: { ...item.value, ...patch } }
      : item
  )

export const markQuestionResolved = (
  timeline: TimelineItem[],
  approvalId: string,
  resolution: QuestionResolution
): TimelineItem[] =>
  timeline.map((item) =>
    item.kind === 'question' && item.approvalId === approvalId
      ? { ...item, status: 'resolved', resolution }
      : item
  )

export type WorkingStep = {
  kind: 'reasoning' | 'tool_call' | 'tool_result' | 'text' | string
  data?: Record<string, unknown>
}

export const workingStepsToTimeline = (
  steps: WorkingStep[] | undefined,
  pending?: { approvalId: string } | null
): TimelineItem[] => {
  const items: TimelineItem[] = []
  const seenCall = new Set<string>()
  const seenResult = new Set<string>()
  const seenReasoning = new Set<string>()
  const seenText = new Set<string>()
  const resultIds = new Set<string>()
  const questionsByCallId = new Map<string, PersistedQuestion>()

  for (const step of steps ?? []) {
    const d = step.data ?? {}
    if (step.kind === 'reasoning') {
      const text = String(d.text ?? '')
      if (!text || seenReasoning.has(text)) continue
      seenReasoning.add(text)
      items.push({ kind: 'reasoning', text })
    } else if (step.kind === 'text') {
      const text = String(d.text ?? '')
      if (!text || seenText.has(text)) continue
      seenText.add(text)
      items.push({ kind: 'text', text })
    } else if (step.kind === 'tool_call') {
      const id = String(d.id ?? '')
      if (id && seenCall.has(id)) continue
      if (id) seenCall.add(id)
      const name = d.name ? String(d.name) : undefined
      items.push({
        kind: 'tool_call',
        name,
        args: toDetail(d.args),
        id: id || undefined
      })
      if (name === 'ask_user' && id) {
        questionsByCallId.set(id, parsePersistedQuestion(id, d.args))
      }
    } else if (step.kind === 'tool_result') {
      const id = String(d.tool_call_id ?? '')
      if (id) {
        resultIds.add(id)
        if (seenResult.has(id)) continue
        seenResult.add(id)
      }
      const name = d.name ? String(d.name) : undefined
      items.push({
        kind: 'tool_result',
        name,
        output: toDetail(d.output),
        id: id || undefined
      })

      const question = id ? questionsByCallId.get(id) : undefined
      const resolution =
        name === 'ask_user' ? parseQuestionResolution(d.output) : null
      if (question && resolution) {
        items.push({
          kind: 'question',
          ...question,
          status: 'resolved',
          resolution
        })
      }
    }
  }

  if (pending?.approvalId) {
    let tool = 'tool'
    let rawArgs: unknown
    for (const step of steps ?? []) {
      if (step.kind !== 'tool_call') continue
      const id = String(step.data?.id ?? '')
      if (!id || resultIds.has(id)) continue
      tool = String(step.data?.name ?? 'tool')
      rawArgs = step.data?.args
      break
    }

    if (tool === 'ask_user') {
      items.push({
        kind: 'question',
        ...parsePersistedQuestion(pending.approvalId, rawArgs),
        status: 'pending'
      })
    } else {
      items.push({
        kind: 'approval',
        approvalId: pending.approvalId,
        tool,
        value: parseJsonObject(rawArgs),
        status: 'pending'
      })
    }
  }

  return items
}

export const finalAnswerText = (items: TimelineItem[]): string =>
  splitTimelineStages(items)
    .map((stage) => timelineStageAnswerText(stage.items))
    .filter(Boolean)
    .join('\n\n')
